import {
  EPS,
  pointOnSegment,
  segmentsFromWaypoints,
  type EdgePathInput,
  type Point,
} from "./routing.ts";

export type Junction = { x: number; y: number };

// Bucket-nyckel kvantiserad med EPS så två endpoints inom toleransen mappar
// till samma plats. Annars kan flyttalsbrus från path-beräkningar dela upp en
// faktisk anslutning på flera nästan-identiska koordinater.
function bucketKey(p: Point): string {
  return `${Math.round(p.x / EPS)}:${Math.round(p.y / EPS)}`;
}

/**
 * Returnerar koordinater där 3+ ledare faktiskt möts (nav) eller där en ledares
 * endpoint landar mitt på en annan ledares segment (T-anslutning). Rena
 * korsningar utan delad endpoint hanteras av computeHops i routing.ts — de
 * renderas som hop-bågar, inte fyllda punkter.
 */
export function computeJunctions(edges: EdgePathInput[]): Junction[] {
  type Endpoint = { edgeId: string; point: Point };
  const endpoints: Endpoint[] = [];
  for (const e of edges) {
    if (e.waypoints.length < 2) continue;
    const first = e.waypoints[0];
    const last = e.waypoints[e.waypoints.length - 1];
    if (first) endpoints.push({ edgeId: e.id, point: first });
    if (last) endpoints.push({ edgeId: e.id, point: last });
  }

  // Steg 1: nav-junctions. Räkna unika edgeIds per bucket — 3+ är ett nav.
  const buckets = new Map<string, { point: Point; edgeIds: Set<string> }>();
  for (const ep of endpoints) {
    const k = bucketKey(ep.point);
    let b = buckets.get(k);
    if (!b) {
      b = { point: ep.point, edgeIds: new Set() };
      buckets.set(k, b);
    }
    b.edgeIds.add(ep.edgeId);
  }

  const junctions: Junction[] = [];
  const navKeys = new Set<string>();
  for (const [k, b] of buckets) {
    if (b.edgeIds.size >= 3) {
      junctions.push({ x: b.point.x, y: b.point.y });
      navKeys.add(k);
    }
  }

  // Steg 2: T-anslutningar. En endpoint som inte redan ligger på ett nav-bucket
  // men som hamnar inuti ett annat edge:s segment är en T-anslutning.
  // segmentsFromWaypoints returnerar [] för waypoints.length < 2 så den är säker
  // att anropa upprepat.
  const segmentsByEdge = new Map(
    edges.map((e) => [e.id, segmentsFromWaypoints(e.waypoints)] as const),
  );

  const tKeys = new Set<string>();
  for (const ep of endpoints) {
    const k = bucketKey(ep.point);
    if (navKeys.has(k) || tKeys.has(k)) continue;
    let onOther = false;
    for (const [otherId, segs] of segmentsByEdge) {
      if (otherId === ep.edgeId) continue;
      for (const seg of segs) {
        if (pointOnSegment(ep.point, seg)) {
          onOther = true;
          break;
        }
      }
      if (onOther) break;
    }
    if (onOther) {
      junctions.push({ x: ep.point.x, y: ep.point.y });
      tKeys.add(k);
    }
  }

  return junctions;
}

/**
 * Bygger en bucket-nyckel-set över junctions. Används av projection-lagret för
 * att filtrera bort hop-bågar som ligger på samma plats som en faktisk
 * anslutning — en junction överröstar en hop visuellt.
 */
export function junctionKeySet(junctions: Junction[]): Set<string> {
  const set = new Set<string>();
  for (const j of junctions) set.add(bucketKey(j));
  return set;
}

export function isPointOnJunction(p: Point, junctionKeys: Set<string>): boolean {
  return junctionKeys.has(bucketKey(p));
}
